import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAR_COPY = {
  1: { label: "Rough", color: "#dc2626" },
  2: { label: "Not great", color: "#ea580c" },
  3: { label: "OK", color: "#eab308" },
  4: { label: "Good", color: "#16a34a" },
  5: { label: "Loved it", color: "#0F4C81" },
};

// Turn a snake_case service ("exterior_lighting") into human-friendly
// display text ("Exterior Lighting"). Empty input → empty string so the
// kicker line just hides gracefully.
function formatService(raw) {
  if (!raw) return "";
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * PublicFeedbackView — the "Feedback First" widget.
 *
 * Landing page linked from every post-completion review email. Splits the
 * customer path by star rating BEFORE any public URL is shown:
 *   • 4-5 stars → show Google + Facebook buttons, one-tap redirect.
 *   • 1-3 stars → private textarea → posts to the team so we can call
 *     the customer before an issue turns into a public 1-star review.
 * Either path silences future review nags on the project.
 */
export default function PublicFeedbackView() {
  const { projectId } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { path, google_review_url, facebook_review_url, message }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/public/feedback/${projectId}`);
        if (!cancelled) setCtx(data);
      } catch (e) {
        if (!cancelled) setError("We couldn't load your project. The link may have expired.");
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  // Auto-fire the 4-5 star submit in the background the moment they pick
  // a high rating, so the Google/Facebook buttons can render inline right
  // below the star row (no intermediate "Almost There" screen).
  useEffect(() => {
    if (rating >= 4 && !result && !submitting) {
      handleSubmit(rating);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rating]);

  const handleSubmit = async (finalRating) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/public/feedback/${projectId}`, {
        rating: finalRating,
        comment,
      });
      setResult(data);
    } catch (e) {
      setError("Sorry — something went wrong saving your feedback. Please text us at " + (ctx?.rep_phone || "") + ".");
    }
    setSubmitting(false);
  };

  if (error) {
    return (
      <Wrapper>
        <h1 style={{ color: "#dc2626", fontSize: 20 }}>Something's off</h1>
        <p style={{ color: "#475569" }}>{error}</p>
      </Wrapper>
    );
  }

  if (!ctx) {
    return <Wrapper><p style={{ color: "#94a3b8" }}>Loading…</p></Wrapper>;
  }

  // 4-5 star path is now rendered INLINE under the star row (no separate
  // screen), so a customer never sees a mid-step "Almost There" panel
  // that could read as a false confirmation. Only the private (low-star)
  // path renders its own end-state screen below.
  if (result?.path === "private") {
    return (
      <Wrapper>
        <h1 style={{ color: "#0F4C81", fontSize: 24, margin: "0 0 10px" }}>Thanks for telling us.</h1>
        <p style={{ color: "#475569", fontSize: 15, lineHeight: 1.6 }}>
          {result.message} If it's urgent, feel free to text{" "}
          <strong>{ctx.rep_phone || "us"}</strong> and we'll get on it right away.
        </p>
      </Wrapper>
    );
  }

  // Landing state — pick a star.
  const stars = [1, 2, 3, 4, 5];
  const display = hover || rating;
  const meta = display ? STAR_COPY[display] : null;
  const isLow = rating > 0 && rating <= 3;

  return (
    <Wrapper>
      <p style={{ color: "#94a3b8", fontSize: 12, textTransform: "uppercase", letterSpacing: 1.5, margin: 0 }}>
        {ctx.company_name}{formatService(ctx.service_type) ? ` · ${formatService(ctx.service_type)}` : ""}
      </p>
      <h1 style={{ color: "#0F172A", fontSize: 28, margin: "8px 0 6px", fontWeight: 800 }}>
        How did we do, {ctx.first_name}?
      </h1>
      {ctx.project_address && (
        <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 24px" }}>{ctx.project_address}</p>
      )}

      <div
        style={{ display: "flex", gap: 8, justifyContent: "center", margin: "8px 0 6px" }}
        data-testid="feedback-star-row"
      >
        {stars.map((n) => {
          const active = display >= n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              data-testid={`feedback-star-${n}`}
              style={{
                background: "transparent", border: "none", cursor: "pointer",
                fontSize: 44, lineHeight: 1, padding: 4,
                color: active ? (meta?.color || "#eab308") : "#e2e8f0",
                transition: "transform .12s, color .12s",
                transform: display === n ? "scale(1.15)" : "scale(1)",
              }}
              aria-label={`${n} star${n === 1 ? "" : "s"}`}
            >
              ★
            </button>
          );
        })}
      </div>

      <p style={{ textAlign: "center", color: meta?.color || "#94a3b8", fontWeight: 600, fontSize: 14, minHeight: 20, margin: "4px 0 20px" }}>
        {meta ? meta.label : "Tap a star"}
      </p>

      {rating > 0 && (
        <div style={{ marginTop: 6 }}>
          {isLow ? (
            <>
              <label style={{ display: "block", fontSize: 13, color: "#334155", fontWeight: 600, margin: "0 0 6px" }}>
                What could we have done better?
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Just typing a few words here goes straight to Chad. Nothing public."
                rows={4}
                data-testid="feedback-comment"
                style={{
                  width: "100%", padding: "12px 14px", border: "1px solid #cbd5e1",
                  borderRadius: 8, fontSize: 14, fontFamily: "inherit", resize: "vertical",
                }}
              />
              <p style={{ color: "#64748b", fontSize: 12, margin: "8px 0 14px" }}>
                Your feedback stays private. We'd rather fix it than see it on Google.
              </p>
              <button
                type="button"
                onClick={() => handleSubmit(rating)}
                disabled={submitting || !comment.trim()}
                data-testid="feedback-submit"
                style={{
                  display: "block", width: "100%",
                  background: submitting ? "#94a3b8" : "#0F4C81",
                  color: "#fff", padding: "14px 22px", borderRadius: 8, border: "none",
                  fontWeight: 700, fontSize: 15,
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: !comment.trim() ? 0.5 : 1,
                }}
              >
                {submitting ? "Sending…" : "Send private feedback"}
              </button>
            </>
          ) : (
            // 4-5 star path renders INLINE — auto-submitted in the
            // background so the buttons appear right below the star row.
            <div>
              <div style={{
                background: "#f0f9ff", border: "1px solid #bae6fd",
                borderRadius: 10, padding: "14px 16px", marginBottom: 14,
              }}>
                <p style={{ margin: 0, fontSize: 14, color: "#0c4a6e", lineHeight: 1.5 }}>
                  Thanks! Your rating hasn't been posted yet. Pick <strong>Google</strong> or <strong>Facebook</strong> below to make it public. Mentioning your <strong>city, state, and service type</strong> (e.g. "roofing, Charleston, SC") helps other homeowners find us.
                </p>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {result?.google_review_url && (
                  <a
                    href={result.google_review_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="feedback-google-btn"
                    style={{
                      flex: 1, minWidth: 200, background: "#0F4C81", color: "#fff",
                      padding: "16px 22px", borderRadius: 8, textAlign: "center", fontWeight: 700,
                      textDecoration: "none", fontSize: 15,
                      boxShadow: "0 4px 12px rgba(15,76,129,0.25)",
                    }}
                  >
                    ★ Post on Google
                  </a>
                )}
                {result?.facebook_review_url && (
                  <a
                    href={result.facebook_review_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="feedback-fb-btn"
                    style={{
                      flex: 1, minWidth: 200, background: "#1877F2", color: "#fff",
                      padding: "16px 22px", borderRadius: 8, textAlign: "center", fontWeight: 700,
                      textDecoration: "none", fontSize: 15,
                      boxShadow: "0 4px 12px rgba(24,119,242,0.25)",
                    }}
                  >
                    👍 Post on Facebook
                  </a>
                )}
              </div>
              {!result && (
                <p style={{ color: "#94a3b8", fontSize: 12, margin: "12px 0 0", textAlign: "center" }}>
                  Setting up your review buttons…
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <div style={{
      minHeight: "100vh", background: "#f1f5f9", padding: "48px 20px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <div style={{
        maxWidth: 520, margin: "0 auto", background: "#fff",
        padding: "36px 32px", borderRadius: 14, boxShadow: "0 8px 40px rgba(15,23,42,0.08)",
      }}
      data-testid="feedback-widget-card">
        {children}
      </div>
    </div>
  );
}
